import {
  DeviceOnGridType,
  IArea, IAttachedPowerTag, IDeviceInSegment, IDevicesPlacePosition,
  IField,
  IGrid, IGridPositionIndex,
  IMousePosition, IMoveSegment, IMovingDevice,
  IPlacedDevice,
  IPosition, IRemovedDeviceFromSwitchboard, IRepeatingBackgrounds, IResizeWidget,
  IRow, ISwitchboardGrid,
  PinStatus
} from '../models/grid.model';
import { DeviceOptions } from '../models/devices.constants';
import {
  ICompatiblePowerTag,
  IProduct, IProductCharacteristicsRules,
  IProductIDs
} from '../../shared/models/product.model';
import { IRectangle, Rectangle } from '../../widget-grid/models/Rectangle.model';
import {
  getBridgeWidth,
} from './devices.helper';
import { IDeviceConfigOptions } from '../models/device-data.model';
import {
  SLOT_PER_MODULE, SPECIAL_DEVICES_LABELS_WIDTH_IN_PERCENTS,
  WIDTH_OF_ONE_MODULE_IN_MM
} from '../models/enclosure.constants';
import { EnclosureTypes } from '../models/enclosure-type.model';
import { PmcContainsEmptySpacesPipe } from '../pipes/pmc-contains-empty-spaces.pipe';
import { IEnclosure } from '../../shared/models/enclosure.model';
import { DeviceImageConstants } from '../../ui/constants/device-image.constants';
import { GridToDeviceSizePipe } from '../pipes/grid-to-device-size.pipe';
import { getPercentOf, heightOfElectricalClamps } from './positions-calculations.helper';
import { IsPatchPanelPipe } from '../pipes/is-patch-panel.pipe';
import { getDevicesFieldsWidth, poleAndRangeIdsAreEqual } from './product.helper';
import {
  EASY_NET_DEVICE_ID,
  PATCHPANEL_16_PORT_DEVICE_ID,
  PATCHPANEL_16_PORT_SHIELDED_DEVICE_ID,
  PATCHPANEL_8_PORT_DEVICE_ID
} from '../models/devices.constants';
import { IDevice } from '../../shared/models/device.model';
import { PowerTagDevicesPipe } from '../pipes/power-tag-devices.pipe';

export const isDeviceCombBusbar = (device: IProduct): boolean => {
  return device?.productCharacteristicsRules?.isCombBusBar;
};

export const deviceHasBridge = (placedDevice: Partial<IPlacedDevice>): boolean => {
  if (placedDevice?.options?.includes(DeviceOptions.NO_BRIDGE)) {
    return false;
  }

  return getBridgeWidth(placedDevice?.device) !== 0;
};

export const removeWidgetsFromGroup = (currentDeviceUUID: string[], placedDevices: IPlacedDevice[]): IPlacedDevice[] => {
  return placedDevices.filter(placedDevice => {
    return !currentDeviceUUID?.some(uuid => uuid === placedDevice.uuid)
  });
};

export const overlappingPositions = (fieldsOnTheSameRow: IField[], movingDevicesUUIDs: string[],
                                     area: IArea, placedDevices: IPlacedDevice[], copyDevice: boolean): number[] => {
  const notAvailablePositions = [];
  const withoutCurrentDevice = copyDevice ? placedDevices : removeWidgetsFromGroup(movingDevicesUUIDs, placedDevices);

  fieldsOnTheSameRow.forEach(field => {
    const deviceOnField = withoutCurrentDevice.find(placedDevice =>
      placedDevice.position.left === field.position.left && placedDevice.position.top === field.position.top);
    if (deviceOnField) {
      const mostLeftUnavailablePosition = deviceOnField.position.left - area.width + 1 > 1 ?
        deviceOnField.position.left - area.width + 1 : 1;
      const mostRightUnavailablePosition = deviceOnField.position.left + deviceOnField.width;
      for (let i = mostLeftUnavailablePosition; i < mostRightUnavailablePosition; i++) {
        notAvailablePositions.push(i);
      }
    }
  });
  return notAvailablePositions;
};

export const isOverlappingAnotherDevicesPosition = (fieldsOnTheSameRow: IField[],
                                                    movingDevice: IPlacedDevice,
                                                    area: IArea,
                                                    placedDevices: IPlacedDevice[],
                                                    movingSegment: IMovingDevice[],
                                                    copyDevice = false): boolean => {
  if (deviceHasBridge(movingDevice) && area.position.left - getBridgeWidth(movingDevice.device) <= 0) {
    return true;
  }
  const notAvailablePositions = overlappingPositions(
    fieldsOnTheSameRow,
    movingSegment.map(deviceInSegment => deviceInSegment.uuid),
    area,
    placedDevices,
    copyDevice);

  return !!notAvailablePositions.find(position => position === area.position.left);
};

export const overlappingDeviceOnRightSide = (movingDevices: Partial<IMovingDevice>[],
                                             area: IArea, placedDevices: IPlacedDevice[], pasteDevice = false): IPlacedDevice => {
  const withoutCurrentDevice = pasteDevice ? placedDevices : removeWidgetsFromGroup(movingDevices.map(d => d.uuid), placedDevices);

  const devicesOnRight = withoutCurrentDevice.filter(placedDevice =>
    (area.position.left <= placedDevice.position.left && area.position.left + area.width > placedDevice.position.left)
    && area.position.top === placedDevice.position.top);
  if (!devicesOnRight.length) {
    return null;
  }
  return devicesOnRight.reduce((deviceA, deviceB) => {
      return deviceA.position.left < deviceB.position.left ? deviceA : deviceB;
    }
  );
};

export const railsWidthTooShortForPlacingDevice = (selectedDeviceWidth: number, placePosition: IPosition, railsWidth: number): boolean => {
  return (placePosition.left + selectedDeviceWidth - 1 > railsWidth);
};

export const distanceBetweenTwoPositions = (leftDevicePosition: IPosition, rightDevicePosition: IPosition): number => {
  return rightDevicePosition.left - leftDevicePosition.left;
};

export const getCombusBusbarDevices = (devices: IPlacedDevice[]): IPlacedDevice[] =>  {
  return devices.filter(placedDevice => placedDevice.device.productCharacteristicsRules?.isCombBusBar);
};

export const getRailsDevices = (devices: IPlacedDevice[]): IPlacedDevice[] => {
  return devices.filter(placedDevice =>
    !placedDevice.device.productCharacteristicsRules?.isCombBusBar &&
    !placedDevice.device.productCharacteristicsRules?.isPowerTag);
};

export const indexOfPlacedDevice = (searchDevice: IPlacedDevice, placedDevices: IPlacedDevice[]): number => {
  return placedDevices.findIndex(placedDevice =>
    placedDevice.uuid === searchDevice.uuid
  );
};

export const isPointOverDropContainer = (pointerPosition: IMousePosition, dropContainer: Rectangle): boolean => {
  return pointerPosition.x >= dropContainer.left && pointerPosition.x <= dropContainer.left + dropContainer.width
    && pointerPosition.y >= dropContainer.top && pointerPosition.y <= dropContainer.top + dropContainer.height;
};

export const deviceSizeNotFitting = (product: IProduct, gridModuleNumbers: number[]): boolean => {
  return gridModuleNumbers.findIndex(numberOfModules => numberOfModules >= product.sizeRules?.minRequiredNumberOfModules) === -1;
};

export const patternFits = (fieldPosition: IPosition, width: number, fittingGrid: IGrid, pattern: PinStatus[]): boolean => {
  let isFitting = true;

  for (let fieldLeft = fieldPosition.left, patternIndex = 0; fieldLeft < fieldPosition.left + width; fieldLeft++, patternIndex++) {
    const fittingGridFieldPosition = fittingGrid.rows[fieldPosition.top - 1]?.fields[fieldLeft - 1];

    if (!fittingGridFieldPosition) {
      break;
    }
    if (pattern[patternIndex] === PinStatus.universal || fittingGridFieldPosition.fieldPinStatus === PinStatus.universal) {
      continue;
    }
    if (
      (pattern[patternIndex] === PinStatus.half_phase_left
        || pattern[patternIndex] === PinStatus.half_phase_right
        || pattern[patternIndex] === PinStatus.half_neutral_left
        || pattern[patternIndex] === PinStatus.half_neutral_right

        || pattern[patternIndex] === PinStatus.half_single_phase_left
        || pattern[patternIndex] === PinStatus.half_single_phase_right
        || pattern[patternIndex] === PinStatus.half_single_neutral_left
        || pattern[patternIndex] === PinStatus.half_single_neutral_right
      )) {
      if (fittingGridFieldPosition.fieldPinStatus === pattern[patternIndex] ||
        fittingGridFieldPosition.fieldPinStatus === PinStatus.free
      ) {
        continue;
      } else {
        isFitting = false;
        break;
      }
    }
    if (
      fittingGridFieldPosition.fieldPinStatus === PinStatus.free ||
      pattern[patternIndex] === PinStatus.gap ||
      fittingGridFieldPosition.fieldPinStatus === PinStatus.gap ||
      fittingGridFieldPosition.fieldPinStatus === pattern[patternIndex]) {
      if (fittingGridFieldPosition.fieldPinStatus !== PinStatus.half_neutral_left &&
        fittingGridFieldPosition.fieldPinStatus !== PinStatus.half_neutral_right &&
        fittingGridFieldPosition.fieldPinStatus !== PinStatus.half_phase_left &&
        fittingGridFieldPosition.fieldPinStatus !== PinStatus.half_phase_right &&
        fittingGridFieldPosition.fieldPinStatus !== PinStatus.half_single_phase_left &&
        fittingGridFieldPosition.fieldPinStatus !== PinStatus.half_single_phase_right &&
        fittingGridFieldPosition.fieldPinStatus !== PinStatus.half_single_neutral_left &&
        fittingGridFieldPosition.fieldPinStatus !== PinStatus.half_single_neutral_right
      ) {
        continue;
      }
    }
    isFitting = false;
    break;
  }
  return isFitting;
};

export const availableHalfDevicePoints = (placedDeviceProduct: IProduct,
                                          placedDeviceLeft: number,
                                          fieldPosition: IPosition,
                                          width: number)
  : { combLeftAllowedOnHalfOfDevice: boolean, combRightAllowedOnHalfOfDevice: boolean } => {
  let combLeftAllowedOnHalfOfDevice = false;
  let combRightAllowedOnHalfOfDevice = false;

  if (placedDeviceProduct?.gridPlacementRules?.halfPointsPlacement?.length) {
    combLeftAllowedOnHalfOfDevice =
      placedDeviceProduct.gridPlacementRules.halfPointsPlacement.findIndex(point => placedDeviceLeft + point === fieldPosition.left) !== -1;
    combRightAllowedOnHalfOfDevice =
      placedDeviceProduct.gridPlacementRules.halfPointsPlacement
        .findIndex(point => placedDeviceLeft + point === fieldPosition.left + width) !== -1;
  }

  return {combLeftAllowedOnHalfOfDevice, combRightAllowedOnHalfOfDevice};
};

export const devicesLeftPointIsBetweenAnotherDevicesWidth = (placedDeviceLeft: number,
                                                             placedDeviceWidth: number,
                                                             fieldPositionLeft: number): boolean => {
  const placedDeviceRight = placedDeviceLeft + placedDeviceWidth;
  return (placedDeviceLeft < fieldPositionLeft && placedDeviceRight > fieldPositionLeft);
};

export const devicesRightPointIsBetweenAnotherDevicesWidth = (placedDevicePositionLeft: number,
                                                              placedDeviceWidth: number,
                                                              fieldPosition: IPosition,
                                                              fieldDeviceWidth: number): boolean => {
  const placedDeviceRight = placedDevicePositionLeft + placedDeviceWidth;
  const fieldRight = fieldPosition.left + fieldDeviceWidth;

  return (fieldRight > placedDevicePositionLeft && fieldRight < placedDeviceRight);
};

export const isPlacedOnHalfOfAnotherDevice = (fieldPosition: IPosition, width: number, placedDevices: IPlacedDevice[]): boolean => {
  return placedDevices.findIndex(placedDevice => {
    const bridgeWidth = getBridgeWidth(placedDevice.device);
    const placedDevicesWidth = placedDevice.width + bridgeWidth;
    const { combLeftAllowedOnHalfOfDevice, combRightAllowedOnHalfOfDevice } =
      availableHalfDevicePoints(placedDevice.device, placedDevice.position.left, fieldPosition, width);

    // eslint-disable-next-line max-len
    return (devicesLeftPointIsBetweenAnotherDevicesWidth(placedDevice.position.left - bridgeWidth, placedDevicesWidth, fieldPosition.left) && !combLeftAllowedOnHalfOfDevice) ||
      // eslint-disable-next-line max-len
      (devicesRightPointIsBetweenAnotherDevicesWidth(placedDevice.position.left - bridgeWidth, placedDevicesWidth, fieldPosition, width) && !combRightAllowedOnHalfOfDevice);
  }) !== -1;
};

// TODO move logic to database
export const fieldIsMediaField = (fieldReference: string): boolean => {
  return new RegExp('^VME...M0').test(fieldReference) || new RegExp('^SZVX..RM71.0').test(fieldReference);
};

// TODO move logic to database
export const fieldIsNonBreakableCoversField = (fieldReference: string): boolean => {
  return new RegExp('^PME...Y0').test(fieldReference);
};

const disablePlacingDevicesOnRail = (onlyAllowedDevicesRangesIDs: number[], deviceRangeID: number): boolean => {
  if (!onlyAllowedDevicesRangesIDs?.length) {
    return false;
  }
  return onlyAllowedDevicesRangesIDs.findIndex(rangeID => rangeID === deviceRangeID) === -1;
};

export const isPlacedOnHalfOfCombBar = (fieldPosition: IPosition,
                                        width: number,
                                        placedDevices: IPlacedDevice[],
                                        device: IProduct): boolean => {
  return placedDevices.findIndex(placedDevice => {
    const bridgeWidth = getBridgeWidth(device);
    const { combLeftAllowedOnHalfOfDevice, combRightAllowedOnHalfOfDevice } =
      // eslint-disable-next-line max-len
      availableHalfDevicePoints(device, fieldPosition.left + bridgeWidth, { top: placedDevice.position.top, left: placedDevice.position.left }, placedDevice.width);
    // eslint-disable-next-line max-len
    return ((devicesLeftPointIsBetweenAnotherDevicesWidth(fieldPosition.left, width, placedDevice.position.left) && !combLeftAllowedOnHalfOfDevice) ||
      // eslint-disable-next-line max-len
      (devicesRightPointIsBetweenAnotherDevicesWidth(fieldPosition.left, width, placedDevice.position, placedDevice.width)  && !combRightAllowedOnHalfOfDevice));
  }) !== -1;
};

export const getDeviceFromRightSide = (placedDevices: IPlacedDevice[], fieldPosition: IPosition, deviceWidth: number): IPlacedDevice => {
  const devicesOnSameRow = placedDevices.filter(placedDevice =>
    !placedDevice?.device?.productCharacteristicsRules?.isPowerTag &&
    !placedDevice?.device?.productCharacteristicsRules?.isCombBusBar && fieldPosition.top === placedDevice.position.top);

  return devicesOnSameRow.find(deviceOnSameRow =>
    deviceOnSameRow.position.left === fieldPosition.left + deviceWidth
  );
};

export const deviceCanBePlacedOnBridge = (deviceWithBridge: IProduct, device: IProduct): boolean => {
  return deviceWithBridge?.bridge?.placeableDevices?.some(placeableDevice =>
    poleAndRangeIdsAreEqual(placeableDevice, device));
}

export const canPlaceDeviceLeftTo = (placedDevices: IPlacedDevice[], field: IField, currentDevice: Partial<IPlacedDevice>): boolean => {
  const currentDevicesWidth = currentDevice?.width ? currentDevice.width : Number(currentDevice?.device?.modules);
  const deviceOnRight = getDeviceFromRightSide(placedDevices, field.position, currentDevicesWidth);
  if (deviceOnRight) {
    if (!deviceHasBridge(deviceOnRight)) {
      return true;
    }

    // Device from the right side has bridge
    if(!deviceCanBePlacedOnBridge(deviceOnRight?.device, currentDevice?.device)) {
      return false;
    }

    return !(getAttachedPowerTagOfDevice(deviceOnRight, placedDevices) && getAttachedPowerTagOfDevice(currentDevice, placedDevices));
  }

  const devicesWithBridgeOnSameRow = placedDevices.filter(placedDevice =>
    // eslint-disable-next-line max-len
    !placedDevice?.device?.productCharacteristicsRules?.isCombBusBar && field.position.top === placedDevice.position.top && deviceHasBridge(placedDevice));
  if (!devicesWithBridgeOnSameRow.length) {
    return true;
  }
  // Check if it's on half of another device
  return devicesWithBridgeOnSameRow.findIndex(deviceWithBridge => {
    const bridgeWidth = getBridgeWidth(deviceWithBridge.device);

    return deviceWithBridge.position.left - bridgeWidth - currentDevicesWidth < field.position.left &&
      field.position.left < deviceWithBridge.position.left;
  }) === -1;
};

export const devicesLeftIsOnHalfOfAnother = (devicesOnSameRow: IPlacedDevice[], fieldLeft: number, deviceWidth: number): boolean => {
  const devicesRight = fieldLeft;
  const devicesLeft = fieldLeft - deviceWidth;
  const hasCovering = devicesOnSameRow.findIndex(placedDevice => {
    const placedDeviceRight = placedDevice.position.left + placedDevice.width;
    return devicesRight > placedDeviceRight && (
      (placedDevice.position.left < devicesLeft && placedDeviceRight > devicesLeft) ||
      (devicesLeft <= placedDevice.position.left)
    );
  });

  return hasCovering !== -1;
};

export const canPlaceDeviceRightTo = (placedDevices: IPlacedDevice[], field: IField, device: Partial<IPlacedDevice>): boolean => {
  const devicesOnSameRow = placedDevices.filter(placedDevice =>
    !placedDevice?.device?.productCharacteristicsRules?.isCombBusBar &&
    !placedDevice?.device?.productCharacteristicsRules?.isPowerTag &&
    field.position.top === placedDevice.position.top);
  const coveringDevice = devicesOnSameRow.find(deviceOnSameRow =>
    deviceOnSameRow.position.left + deviceOnSameRow.width === field.position.left
  );
  if (!coveringDevice) {
    return !devicesLeftIsOnHalfOfAnother(devicesOnSameRow, field.position.left, getBridgeWidth(device?.device));
  } else {
    if (!deviceHasBridge(device)) {
      return true;
    }

    if (!deviceCanBePlacedOnBridge(device?.device, coveringDevice?.device)) {
      return false;
    }

    return !(getAttachedPowerTagOfDevice(device, placedDevices) && getAttachedPowerTagOfDevice(coveringDevice, placedDevices));
  }
};

const generateAvailablePositionsForCombBarDevices = (placedDevice: Partial<IPlacedDevice>,
                                                     width: number,
                                                     placedDevices: IPlacedDevice[],
                                                     combGrid: IGrid,
                                                     devicesGrid: IGrid): IPosition[] => {
  const availablePositions: IPosition[] = [];

  const combPattern = placedDevice.device.pattern;
  combGrid.rows.forEach(row => {
    if (row.adjustedGridToDevices?.length) {
      // Rows with adjustable grids are not available for comb-bar devices
      return;
    }
    row.fields.forEach(field => {
      // TODO create notCombDevicesOnSameRow function
      const devicesOnSameRow = placedDevices.filter(placedDeviceOnGrid =>
        !placedDeviceOnGrid?.device?.productCharacteristicsRules?.isPowerTag &&
        !placedDeviceOnGrid?.device?.productCharacteristicsRules?.isCombBusBar && field.position.top === placedDeviceOnGrid.position.top);
      if (patternFits(field.position, width, devicesGrid, combPattern)
        && !isPlacedOnHalfOfAnotherDevice(field.position, width, devicesOnSameRow)) {
        availablePositions.push(field.position);
      }
    });
  });
  return availablePositions;
};

export const getCompatiblePowerTagPosition = (compatiblePowerTags: ICompatiblePowerTag[],
                                              powerTagProduct: IProduct): number => {
  return compatiblePowerTags?.find(compatiblePowerTag => poleAndRangeIdsAreEqual({
    productPoleId: compatiblePowerTag.productPoleId,
    productRangeId: compatiblePowerTag.productRangeId
  }, powerTagProduct))?.positionRelativeToConnectedDevice
}

export const generateAvailablePositionsForPowerTagDevices = (placedPowerTagDevice: Partial<IPlacedDevice>,
                                                             placedDevices: IPlacedDevice[]): IPosition[] => {
  const availablePositions: IPosition[] = [];

  placedDevices?.forEach(placedDevice => {
    // eslint-disable-next-line max-len
    const compatiblePowerTagPosition = getCompatiblePowerTagPosition(placedDevice?.device?.gridPlacementRules?.compatiblePowerTagDevices, placedPowerTagDevice?.device);
    if (compatiblePowerTagPosition !== null && compatiblePowerTagPosition !== undefined) {
      availablePositions.push({
        top: placedDevice.position.top,
        left: placedDevice.position.left + compatiblePowerTagPosition
      });
    }
  })

  return availablePositions;
};

const thereAreRestrictionsOnFieldPlacing = (row: IRow,
                                            fieldPosition: IPosition,
                                            enclosureMountingType: string,
                                            devicesIDs: IProductIDs,
                                            devicesWidth: number): boolean => {
  if (!row.specialFieldPlacementRules) {
    return false;
  }
  return row.specialFieldPlacementRules.findIndex(rule => {
    return rule.enclosureType === enclosureMountingType &&
      (rule.unavailableDevicesReferences.findIndex(unavailableReference =>
        unavailableReference.productRangeId === devicesIDs.productRangeId &&
        unavailableReference.productPoleId === devicesIDs.productPoleId) !== -1) &&
      (fieldPosition.left > Math.min(...rule.unavailableSlots) - devicesWidth &&
        fieldPosition.left <= Math.max(...rule.unavailableSlots))
  }) !== -1
};

const disableEntireRow = (row: IRow, placedDevice: Partial<IPlacedDevice>): boolean => {
  return disablePlacingDevicesOnRail(row.onlyAllowedDevicesRangesIDs,  placedDevice?.device?.productRangeId);
};

const rightExtensionDevicesPlacementRestriction = (placedDevices: IPlacedDevice[],
                                                   field: IField,
                                                   currentDevice: Partial<IPlacedDevice>): boolean => {
  const deviceFromLeft = getDeviceFromLeftSide(placedDevices, field.position);

  if (!currentDevice?.device?.gridPlacementRules?.hasRightExtension) {
    if (!deviceFromLeft) {
      return false;
    }
    if (!deviceFromLeft.device.gridPlacementRules?.hasRightExtension) {
      return false
    }

    const currentDeviceCanBePlacedNextToExtension = deviceFromLeft.device.gridPlacementRules.rightSideExtension.placeableDevicesFromRight
      .some(device => (device.productRangeId === currentDevice.device.productRangeId) &&
        (device.productPoleId === currentDevice.device.poleId));

    return !currentDeviceCanBePlacedNextToExtension;
  }
  const currentDevicesWidth = currentDevice?.width ? currentDevice.width : Number(currentDevice?.device?.modules);
  const deviceOnRight = getDeviceFromRightSide(placedDevices, field.position, currentDevicesWidth);

  if (deviceFromLeft?.device?.gridPlacementRules?.rightSideExtension) {
    // if device from the left side has extension, we cannot place auxiliary on that extension
    return true;
  }
  if (!deviceOnRight) {
    return false;
  }
  const allowedDevicesNextToExtension = currentDevice.device.gridPlacementRules?.rightSideExtension?.placeableDevicesFromRight?.
    some(device => (device.productRangeId === deviceOnRight.device.productRangeId) &&
    (device.productPoleId === deviceOnRight.device.poleId));

  return !allowedDevicesNextToExtension;
}

const canBePlacedAtPosition = (field: IField,
                               row: IRow,
                               bridgeWidth: number,
                               devicesWidth: number,
                               combGrid: IGrid,
                               combBarsOnSameRow: IPlacedDevice[],
                               placedDevice: IProduct,
                               enclosureMountingType: string): boolean => {
  return patternFits({
      top: field.position.top,
      left: field.position.left - bridgeWidth
    }, devicesWidth, combGrid, placedDevice.pattern) &&
    !isPlacedOnHalfOfCombBar({
      top: field.position.top,
      left: field.position.left - bridgeWidth
    }, devicesWidth, combBarsOnSameRow, placedDevice) &&
    !thereAreRestrictionsOnFieldPlacing(row, {
        top: field.position.top,
        left: field.position.left - bridgeWidth
      }, enclosureMountingType,
      { productRangeId: placedDevice?.productRangeId, productPoleId: placedDevice?.poleId },
      devicesWidth);
};

// TODO refactor generateAvailablePositions, divide into availablePositions for comb-bars and another devices
export const generateAvailablePositions = (placedDevice: Partial<IPlacedDevice>,
                                           width: number,
                                           placedDevices: IPlacedDevice[],
                                           combGrid: IGrid,
                                           devicesGrid: IGrid,
                                           enclosureMountingType: string,
                                           selectedDevicesUUIDs?: string[]): IPosition[] => {
  if (placedDevice?.device?.productCharacteristicsRules?.isCombBusBar) {
    return generateAvailablePositionsForCombBarDevices(placedDevice, width, placedDevices, combGrid, devicesGrid);
  }

  const availablePositions: IPosition[] = [];
  // TODO create separated functions for devices with bridge and without
  const hasBridge = deviceHasBridge(placedDevice);
  const bridgeWidth = hasBridge ? getBridgeWidth(placedDevice.device) : 0;
  const devicesWidth = width + bridgeWidth;
  devicesGrid.rows.forEach(row => {
    if (disableEntireRow(row, placedDevice)) {
      return;
    }

    row.fields.forEach(field => {
      const combBarsOnSameRow = placedDevices.filter(placedDeviceOnGrid =>
        placedDeviceOnGrid?.device?.productCharacteristicsRules?.isCombBusBar && field.position.top === placedDeviceOnGrid.position.top);
      // eslint-disable-next-line max-len
      if (canBePlacedAtPosition(field, row, bridgeWidth, devicesWidth, combGrid, combBarsOnSameRow, placedDevice?.device, enclosureMountingType)) {
        const placedDevicesWithoutCurrent = removeWidgetsFromGroup(selectedDevicesUUIDs, placedDevices);
        if (rightExtensionDevicesPlacementRestriction(placedDevicesWithoutCurrent, field, placedDevice)) {
          return;
        }
        if (!hasBridge) {
          if (canPlaceDeviceLeftTo(placedDevicesWithoutCurrent, field, placedDevice)) {
            availablePositions.push(field.position);
          }
        } else {
          if (canPlaceDeviceRightTo(placedDevicesWithoutCurrent, field, placedDevice)
            && canPlaceDeviceLeftTo(placedDevicesWithoutCurrent, field, placedDevice)) {
            availablePositions.push(field.position);
          }
        }
      }
    });
  });

  return availablePositions;
};

export const getDeviceFromLeftSide = (placedDevices: IPlacedDevice[], fieldPosition: IPosition): IPlacedDevice => {
  return  placedDevices.find(placedDevice =>
    !placedDevice?.device?.productCharacteristicsRules?.isCombBusBar &&
    !placedDevice?.device?.productCharacteristicsRules?.isPowerTag &&
    fieldPosition.top === placedDevice.position.top &&
    fieldPosition.left === placedDevice.position.left + placedDevice.width);
};

export const deviceFromLeftFitsToBridge = (device: IProduct, placedDevices: IPlacedDevice[], fieldPosition: IPosition): IPlacedDevice => {
  const deviceOnLeft = getDeviceFromLeftSide(placedDevices, fieldPosition);
  if (!deviceOnLeft) {
    return undefined;
  }

  return deviceCanBePlacedOnBridge(device, deviceOnLeft?.device) ? deviceOnLeft : undefined;
};

export const bridgeDeviceFromRightFitsToDevice = (device: IProduct,
                                                  placedDevices: IPlacedDevice[],
                                                  devicePosition: IPosition, _: number): IPlacedDevice => {
  const deviceOnRight = getDeviceFromRightSide(placedDevices, devicePosition, Number(device.modules));
  if (!deviceOnRight || !deviceOnRight?.device?.productCharacteristicsRules?.isFiVigiClarioIc40) {
    return undefined;
  }
  return deviceCanBePlacedOnBridge(deviceOnRight?.device, device) ? deviceOnRight : undefined;
};

export const findFirstFieldWithNoPin = (fieldPosition: IPosition, width: number, fittingGrid: IGrid): IField => {
  const currentRow = fittingGrid.rows[fieldPosition.top - 1];

  return currentRow.fields.find(currentField =>
    currentField.fieldPinStatus === PinStatus.no_pin &&
    fieldPosition.left < currentField.position.left &&
    currentField.position.left < fieldPosition.left + width);
};

export const removeBridgeFromPattern = (device: IProduct): PinStatus[] => {
  const bridgeWidth = getBridgeWidth(device);
  return device.pattern.slice(bridgeWidth, device.pattern.length);
}

export const generatePatternOfDevice = (placedDevice: Partial<IPlacedDevice>): PinStatus[] => {
  if (placedDevice?.device?.presentationRules?.deviceWithRemoveableBridge && !deviceHasBridge(placedDevice))
  {
    return removeBridgeFromPattern(placedDevice.device);
  }

  return placedDevice.device.pattern;
};

export const isFieldBetweenDevicesWidth = (fieldLeft: number, devicesLeft: number, devicesWidth: number) => {
  return fieldLeft >= devicesLeft && devicesLeft + devicesWidth > fieldLeft;
};

export const getCombBusbarPlacedOnTop = (currentDevice: IPlacedDevice, placedDevices: IPlacedDevice[]): IPlacedDevice => {
  return placedDevices
    .filter(findComb => findComb?.device?.productCharacteristicsRules?.isCombBusBar)
    .find(device =>
    checkIfDeviceIsPlacedUnderCombBusbarByPosition(currentDevice, device)
  );
};

export const checkIfDeviceIsPlacedUnderCombBusbarByPosition = (device: IPlacedDevice, busbar: IPlacedDevice): boolean => {
  if(device?.device?.productCharacteristicsRules?.isSpd || device?.device?.productCharacteristicsRules?.isRCCBFIClarioiLD403PN) {
    return checkIfHalfPlacedDeviceIsPlacedUnderCombBusBar(device, busbar);
  }
  return busbar?.device?.productCharacteristicsRules?.isCombBusBar &&
  gridPositionIndexesAreEqual(busbar.enclosureField, device.enclosureField)
  && busbar.position.top === device.position.top
  && checkIfDevicePositionIsUnderBusbar(device.position.left, busbar.position.left, busbar.width);
}

export const checkIfHalfPlacedDeviceIsPlacedUnderCombBusBar = (device: IPlacedDevice, busbar: IPlacedDevice) => {
  return gridPositionIndexesAreEqual(busbar.enclosureField, device.enclosureField)
    && busbar.position.top === device.position.top
    && checkIfSpdDevicePositionIsUnderBusbar(device.position.left, device.width,  busbar.position.left, busbar.width);
}

/**
 * @TODO See can we reuse checkIfDeviceIsPlacedUnderCombBusbar method for this or only underWhatCombBusBarId
 */
export const checkIfNonCombBusBarDeviceIsPlacedUnderAndConnectedWithCombBusBar = (device: IPlacedDevice, combBusBar: IPlacedDevice) => {
  return !device?.device?.productCharacteristicsRules?.isCombBusBar &&
    checkIfDeviceIsPlacedUnderCombBusbarByPosition(device, combBusBar)
    && device.sldConnection?.underWhatCombBusBarId === combBusBar.id;
}

const checkIfDevicePositionIsUnderBusbar = (devicePositionLeft: number,
                                           busbarPositionLeft: number,
                                           busbarWidth: number) => {
  return (devicePositionLeft >= busbarPositionLeft) && (devicePositionLeft < busbarPositionLeft + busbarWidth);
}

const checkIfSpdDevicePositionIsUnderBusbar = (devicePositionLeft: number,
                                            deviceWidth: number,
                                            busbarPositionLeft: number,
                                            busbarWidth: number) => {
  const devicePositionRight = devicePositionLeft + deviceWidth;
  const combBusBarPositionRight = busbarPositionLeft + busbarWidth;

  return (devicePositionRight > busbarPositionLeft) && (devicePositionRight <= combBusBarPositionRight)
}

const getCombBarEndPosition = (placedDevice: IPlacedDevice) => {
  if (placedDevice && placedDevice?.device?.productCharacteristicsRules?.isCombBusBar) {
    return placedDevice.width + placedDevice.position.left;
  }
};

export const getAvailableFreeSpaceForCustomDevice = (placedDevices: IPlacedDevice[], placedDevice: IPlacedDevice, fieldWidth: number)
  : number => {
  const devicesFromSameRow = placedDevices?.filter(d =>  d.position.top === placedDevice?.position?.top
    && gridPositionIndexesAreEqual(d.enclosureField, placedDevice?.enclosureField));
  const isCoveredByCombbar = getCombBusbarPlacedOnTop(placedDevice, devicesFromSameRow);
  let combBarEndDistance: number;
  if (isCoveredByCombbar) {
    combBarEndDistance = (getCombBarEndPosition(isCoveredByCombbar) - placedDevice.position.left) / 2;
  }
  const devicesFromSameRowToRight = devicesFromSameRow?.filter(d => d.position.left > placedDevice?.position?.left);
  let clostestDeviceDistance: number;
  if (devicesFromSameRowToRight.length > 0) {
    const closestDevice: IPlacedDevice = devicesFromSameRowToRight.reduce((acc, item, _) => {
      return acc.position.left < item.position.left ? acc : item;
    });
    const bridgeWidth = getBridgeWidth(closestDevice.device);
    clostestDeviceDistance = ((closestDevice.position.left - bridgeWidth) - placedDevice.position.left) / 2 ;
  } else {
    clostestDeviceDistance = ((fieldWidth + 1) - placedDevice.position.left) / 2;
  }
  return combBarEndDistance ?
    combBarEndDistance < clostestDeviceDistance ? (combBarEndDistance) : clostestDeviceDistance
    : clostestDeviceDistance;
};

export const getAvailableSizeOfCombBar = (size: number, cuttingSlots: number): number => {
  if(!cuttingSlots) {
    return size;
  }

  return (size % cuttingSlots) === 0 ? size : size - (size % cuttingSlots);
};

export const gridPositionIndexesAreEqual = (indexA: IGridPositionIndex, indexB: IGridPositionIndex): boolean => {
  return (indexA?.top === indexB?.top) && (indexA?.left === indexB?.left);
};

export const getOccupiedRowsAndFields = (
  row: IRow,
  device: { top: number, left: number, width: number },
  pattern: PinStatus[],
  arrayCounter: number) => {
  const rowFields = row.fields.map(field => {
    const newField = { ...field };
    if (isFieldBetweenDevicesWidth(field.position.left, device.left, device.width)) {
      newField.fieldPinStatus = pattern[arrayCounter];
      if (pattern[arrayCounter + 1]) {
        arrayCounter++;
      } else {
        arrayCounter = 0;
      }
    }
    return newField;
  });
  return { ...row, fields: rowFields };
};

export const currentFieldRailsWidth = (fieldNumber: IGridPositionIndex,
                                       devicePosition: IPosition,
                                       devicesEnclosureGrid: IGrid[]): number => {
  const currentGrid = devicesEnclosureGrid.find(grid => gridPositionIndexesAreEqual(grid.enclosureField, fieldNumber));

  return currentGrid.rows[devicePosition.top - 1]?.fields?.length;
};

export const generateAvailableWidthForNotFittingPattern = (field: IField,
                                                           width: number,
                                                           currentGrid: IGrid,
                                                           resizedWidget: IResizeWidget): number => {
  const blockingFieldWithNoPin = findFirstFieldWithNoPin(field.position, width, currentGrid);
  if (!blockingFieldWithNoPin) {
    return resizedWidget.placedDevice.width;
  } else {
    const newSize = getAvailableSizeOfCombBar(blockingFieldWithNoPin.position.left - field.position.left,
      resizedWidget?.placedDevice?.device?.gridPlacementRules?.numberOfCombBusBarCuttingSlots);
    return patternFits(field.position, newSize, currentGrid, resizedWidget.placedDevice.device.pattern) ?
      newSize : resizedWidget.placedDevice.width;
  }
};

export const enclosureHasEmptySpaces = (enclosure: IEnclosure, products: IProduct[]): boolean => {
  if (enclosure?.enclosureType !== EnclosureTypes.PRAGMA && enclosure?.enclosureType !== EnclosureTypes.PRISMA_XS) {
    // If enclosure is not Prisma XS or Pragma Multi Customized
    return false;
  }
  if (enclosure?.enclosureType === EnclosureTypes.PRISMA_XS) {
    // If enclosure is prisma XS
    const enclosureSurface = enclosure?.rows * enclosure?.fields;
    const kitsSumSurface = products?.reduce(
      (kitA, kitB) => kitA + (kitB?.prismaProduct?.fields * kitB?.prismaProduct?.rows), 0);
    return enclosureSurface !== kitsSumSurface
  }
  // If enclosure is Pragma Multi Customized
  const widthOfAllProducts = products.reduce(
    (productA, productB) => productA + productB?.pragmaProduct?.fields, 0);
  return new PmcContainsEmptySpacesPipe().transform(enclosure, widthOfAllProducts);
};


export const deviceIsAlreadySelected = (device: IPlacedDevice,selectedDevices: IPlacedDevice[]): boolean => {
  if (!selectedDevices?.length) {
    return false;
  }
  return selectedDevices.some(selectedDevice => selectedDevice.uuid === device.uuid);
};

export const getClosestWidgetFromLeft = (widgets: IPlacedDevice[]): IPlacedDevice => {
  return widgets.reduce((deviceA, deviceB) => {
    return deviceA.position.left < deviceB.position.left ? deviceA : deviceB;
  });
};

export const getClosestWidgetFromRight = (widgets: IPlacedDevice[]): IPlacedDevice => {
  return widgets.reduce((deviceA, deviceB) => {
    return deviceA.position.left + deviceA.width > deviceB.position.left + deviceB.width ? deviceA : deviceB;
  });
};

export const getClosestWidgetFromTop = (widgets: IPlacedDevice[]): IPlacedDevice => {
  return widgets.reduce((deviceA, deviceB) => {
    return deviceA.position.top < deviceB.position.top ? deviceA : deviceB;
  });
};

export const getClosestWidgetFromBottom = (widgets: IPlacedDevice[]): IPlacedDevice => {
  return widgets.reduce((deviceA, deviceB) => {
    return deviceA.position.top < deviceB.position.top ? deviceB : deviceA;
  });
};

// eslint-disable-next-line max-len
export const getRowFieldsFromGrid = (grids: IGrid[], fieldIndex: IGridPositionIndex, rowNumber: number, movingDevice: IMovingDevice): IField[] => {
  const currentGrid = grids.find(enclosureGrid => gridPositionIndexesAreEqual(enclosureGrid.enclosureField, fieldIndex));
  if (!currentGrid?.rows) {
    return undefined;
  }
  const row = currentGrid.rows[rowNumber-1];
  if (!row?.adjustedGridToDevices) {
    return row?.fields;
  }
  const gridDimensions = new GridToDeviceSizePipe().transform(row, [movingDevice]);
  if (!gridDimensions) {
    return row.fields;
  }
  // Every kit has number of fields as the maximum device that can fit in it.
  // In case if device is smaller then the one with max number of fields, fields number is sliced.
  return row.fields.slice(0, gridDimensions.deviceWidth);
};

export const getCurrentMovingDevice = (movingSegmentLength: number,
                                       movingDevice: IDeviceInSegment,
                                       movingDevices: IMovingDevice[]): IMovingDevice => {
  return movingSegmentLength === 1 ?
    movingDevices[0] :
    movingDevices.find(device => device.uuid === movingDevice.deviceUUID)
};

export const positionDoesNotExist = (position: IDevicesPlacePosition): boolean => {
  return !position?.top || !position?.left;
};

export const updateCustomDevicesWidth = (movingDevices: IMovingDevice[]): IMovingDevice[] => {
  return movingDevices.map(movingDevice => {
    if (movingDevice?.device?.productCharacteristicsRules?.isCustomDevice) {
      return {
        ...movingDevice,
        device: {
          ...movingDevice.device,
          width: movingDevice.width
        }
      }
    } else {
      return { ...movingDevice }
    }
  })
};


export const widgetIsCurrentSelected = (widgetUUID: string,
                                        selectedDeviceWidgets: IPlacedDevice[],
                                        selectedDeviceWidgetIndex: number): boolean => {
  if (!selectedDeviceWidgets?.length) {
    return false;
  }
  return selectedDeviceWidgets[selectedDeviceWidgetIndex]?.uuid === widgetUUID;
};

export const getDevicesInBetween = (selectedDeviceWidgets: IPlacedDevice[],
                                    placedDevices: IPlacedDevice[],
                                    selectedGrid: IGrid,
                                    selectedDevice: IPlacedDevice,
                                    selectedDeviceIsCombBar: boolean): IPlacedDevice[] => {
  const mostTopDevice = getClosestWidgetFromTop(selectedDeviceWidgets);
  const mostLeftInFirstRowPosition = getClosestWidgetFromLeft(
    selectedDeviceWidgets
      .filter(d => d.position.top === mostTopDevice.position.top)).position;
  if (selectedDevice.position.top < mostLeftInFirstRowPosition.top || (selectedDevice.position.left < mostLeftInFirstRowPosition.left &&
    selectedDevice.position.top === mostLeftInFirstRowPosition.top)) {
    const mostBottomDevice = getClosestWidgetFromBottom(selectedDeviceWidgets);
    const mostRightInFirstRowPosition = getClosestWidgetFromRight(
      selectedDeviceWidgets
        .filter(d => d.position.top === mostBottomDevice.position.top)).position;

    // eslint-disable-next-line max-len
    return getDevicesBetweenTwo(selectedDevice?.position, mostRightInFirstRowPosition, selectedDevice.enclosureField, selectedGrid, selectedDeviceIsCombBar, placedDevices);
  }

  // eslint-disable-next-line max-len
  return getDevicesBetweenTwo(mostLeftInFirstRowPosition, selectedDevice?.position, selectedDevice.enclosureField, selectedGrid, selectedDeviceIsCombBar, placedDevices);

};

export const getDevicesBetweenTwo = (firstDevicePosition: IPosition,
                                     secondDevicePosition: IPosition,
                                     movingDeviceEnclosureField: IGridPositionIndex,
                                     selectedGrid: IGrid,
                                     selectedDeviceIsCombBar: boolean,
                                     placedDevices: IPlacedDevice[]): IPlacedDevice[] => {
  const selectedWidgets: IPlacedDevice[] = [];
  for (let row = firstDevicePosition.top; row <= secondDevicePosition.top; row ++) {
    const startPositionInRow = row === firstDevicePosition.top ? firstDevicePosition.left : 1
    const endPositionInRow = row === secondDevicePosition.top ? secondDevicePosition.left : selectedGrid.rows[row].fields.length;
    for (let slot = startPositionInRow; slot <= endPositionInRow; slot ++) {
      const deviceOnPosition = placedDevices.find(placedDevice => {
        return gridPositionIndexesAreEqual(placedDevice.enclosureField, movingDeviceEnclosureField) &&
          placedDevice.position.top === row &&
          placedDevice.position.left === slot &&
          selectedDeviceIsCombBar === placedDevice?.device?.productCharacteristicsRules?.isCombBusBar &&
          !placedDevice?.device?.productCharacteristicsRules?.isPowerTag
      });

      if (deviceOnPosition) {
        selectedWidgets.push(deviceOnPosition);
      }
    }
  }
  return selectedWidgets;
}

export const generateDevicesPositionsInSegment = (selectedDevices: IPlacedDevice[], segmentPosition: IRectangle): IDeviceInSegment[] => {
  const devicesPositionsOfDevicesInSegment: IDeviceInSegment[] = []
  selectedDevices.forEach(device => {
    devicesPositionsOfDevicesInSegment.push({
      position: {
        left: device.position.left - segmentPosition.left,
        top: device.position.top - segmentPosition.top
      },
      deviceUUID: device.uuid
    });
  });

  return devicesPositionsOfDevicesInSegment;
};

export const getDeviceByPosition = (devicePosition: IPosition,
                                    gridPosition: IGridPositionIndex,
                                    devices: IPlacedDevice[]): IPlacedDevice => {
  return devices.find(device => gridPositionIndexesAreEqual(device.enclosureField, gridPosition) &&
    devicePosition.top === device.position.top && devicePosition.left === device.position.left)
};

export const deviceIsNotSelectableBecauseOfRowsRestrictions = (devicesEnclosureGrid: IGrid[],
                                                               selectedDeviceWidget: IPlacedDevice,
                                                               device: IPlacedDevice): boolean => {
  const enclosureGrid = devicesEnclosureGrid.find(grid => {
    return gridPositionIndexesAreEqual(grid.enclosureField, selectedDeviceWidget.enclosureField);
  });
  const currentRow = enclosureGrid.rows.find(row => row.rowNumber === device.position.top);

  if (currentRow?.adjustedGridToDevices?.length) {
    return true;
  }
  if (enclosureGrid.canSelectDevicesBetweenRows) {
    return false;
  }

  return selectedDeviceWidget.position.top !== device.position.top;
};

export const getSegmentPositionsForSpecialGrids = (movingSegment: IMoveSegment,
                                                   devicesEnclosureGrid: IGrid[],
                                                   referentDeviceGridIndex: IGridPositionIndex): IMoveSegment => {
  const grid = devicesEnclosureGrid.find(g => gridPositionIndexesAreEqual(g.enclosureField, referentDeviceGridIndex));

  if (!grid || grid?.canSelectDevicesBetweenRows) {
    return movingSegment;
  }
  return {
    ...movingSegment,
    movingDevices: movingSegment.movingDevices.map(d => {
      if (d.position.top === 0) {
        return d
      }
      return {
        ...d,
        position: {
          ...d.position,
          top: d.position.top * 2
        }
      }
    })
  }
};

export const wholeSegmentIsNotSelectable = (combEnclosureGrid: IGrid[],
                                            devicesEnclosureGrid: IGrid[],
                                            selectedDevices: IPlacedDevice[]): boolean => {
  const [referentDevice] = selectedDevices;
  if (!referentDevice) {
    return true;
  }
  const currentGrid = referentDevice?.device?.productCharacteristicsRules?.isCombBusBar ?
    combEnclosureGrid.find(grid => gridPositionIndexesAreEqual(grid.enclosureField, referentDevice.enclosureField)) :
    devicesEnclosureGrid.find(grid => gridPositionIndexesAreEqual(grid.enclosureField, referentDevice.enclosureField));

  return !currentGrid?.canSelectDevicesBetweenRows &&
    selectedDevices.some(device => referentDevice.position.top !== device.position.top);
};

export const devicePlacedOnBridgeLabelOffset = (widgetElementClientWidth: number,
                                                deviceWidth: number,
                                                widgetWithBridgeFromRightWidth: number): number => {
  const widthOfOneField = widgetElementClientWidth / deviceWidth;
  const leftCurrentOffset = ((widthOfOneField * deviceWidth) / 2);
  const offsetOfDevicePlacedOnBridge = ((widthOfOneField * (deviceWidth + widgetWithBridgeFromRightWidth)) / 2);

  return offsetOfDevicePlacedOnBridge - leftCurrentOffset;
};

export const getNumberOfClampsInSegment = (device: IProduct, placedWidgetWidth: number): number => {
  return Math.floor(( placedWidgetWidth / 2 ) * (WIDTH_OF_ONE_MODULE_IN_MM / getElectricalClampWidthInMM(device)));
}

export const getWidthOfClampsSegment = (device: IProduct,
                                        numberOfClampDevicesInSegment: number,
                                        widgetWidthOnGrid: number,
                                        widgetWidthInSlots: number): number => {
  const clampWidthInMM = getElectricalClampWidthInMM(device);
  // eslint-disable-next-line max-len
  const width = ((clampWidthInMM * numberOfClampDevicesInSegment) * widgetWidthOnGrid) / ((WIDTH_OF_ONE_MODULE_IN_MM * widgetWidthInSlots) / SLOT_PER_MODULE);

  return width / numberOfClampDevicesInSegment;
}

export const getWidthInSlotsForClamp = (device: IProduct, numberOfClampDevicesInSegment: number): number => {
  if(!device || !numberOfClampDevicesInSegment) {
    return;
  }
  const clampWidthInMM = getElectricalClampWidthInMM(device);
  return Math.ceil((numberOfClampDevicesInSegment * clampWidthInMM) / (WIDTH_OF_ONE_MODULE_IN_MM / 2));
}

export const getElectricalClampWidthInMM = (device: IProduct): number => {
  switch (device.productId) {
    case 1346:
      // Electrical Clamp 3 LN: blue gray gray gray
      return 24;
    case 1347:
      // Electrical Clamp 1 LN: blue gray
      return 12;
    case 1348:
      // Electrical Clamp 3 LN PE: yellow/green blue gray gray gray
      return 30;
    case 1349:
      // Electrical Clamp 1 LN PE: yellow/green blue gray
      return 18;
    default: {
      return WIDTH_OF_ONE_MODULE_IN_MM;
    }
  }
};

export const getRepeatingBackgrounds = (device: IProduct,
                                        placedWidgetWidth: number,
                                        widgetWidth: number,
                                        containerHeight: number,
                                        backgroundImage: string,
                                        heightOfElectricalClamp: number): IRepeatingBackgrounds => {
  const numberOfClampDevicesInSegment = getNumberOfClampsInSegment(device, placedWidgetWidth);
  const widthOfOneInSegment = getWidthOfClampsSegment(device, numberOfClampDevicesInSegment, widgetWidth, placedWidgetWidth);

  let backgrounds = '';
  let backgroundSize = '';
  for (let i = 0; i < numberOfClampDevicesInSegment; i ++) {
    backgrounds += `${ backgroundImage } ${ i * widthOfOneInSegment }px 0 no-repeat ${ i !== numberOfClampDevicesInSegment - 1 ? ',' : '' }`
    backgroundSize += `${ widthOfOneInSegment }px ${ heightOfElectricalClamp }px ${ i !== numberOfClampDevicesInSegment - 1 ? ',' : '' }`
  }

  return { backgrounds, backgroundSize };
}

const checkIfDeviceIsBridgedContactor = (product: IProduct, hasBridge: boolean): boolean => {
  return hasBridge && product?.productCharacteristicsRules?.isBridgedContactor;
}

export const getProductSupportSpecBridgeWidth = (product: IProduct,
                                                 productSupportSpecWidth: number,
                                                 deviceWidth: number,
                                                 bridgeWidth: number,
                                                 hasBridge: boolean): number => {
  return checkIfDeviceIsBridgedContactor(product, hasBridge) ? DeviceImageConstants.SWITCHING_DEVICE_BRIDGE_WIDTH :
    bridgeWidth ? bridgeWidth *  (productSupportSpecWidth / deviceWidth) : 0;
}

export const chcekIfMeteringDeviceIsPlacedOnMeteringKit = (device: IPlacedDevice, enclosureField: IGrid): boolean => {
  if(!device || !enclosureField) {
    return;
  }
  const kitRow = enclosureField.rows.find(row => row?.rowNumber === device?.position?.top);

  return kitRow?.adjustedGridToDevices.findIndex(a => a.deviceReference === device?.device?.reference) >= 0;
}

export const getEnclosureDeviceGrid = (enclosureField: IGridPositionIndex, devicesEnclosureGrid: IGrid[]): IGrid => {
  return devicesEnclosureGrid?.find(d => gridPositionIndexesAreEqual(d.enclosureField, enclosureField));
}

export const getWidgetOnSwbHeight = (device: IProduct,
                                     containerHeight: number,
                                     enclosurePictureHeight: number,
                                     heightOfOneRail: number,
                                     widgetWidth: number,
                                     productSupportSpecWidth: number): number => {
  if (device?.productCharacteristicsRules?.isElectricalClamp) {
    return heightOfElectricalClamps(containerHeight, enclosurePictureHeight);
  }
  if (device?.productCharacteristicsRules?.isCombBusBar || device?.productCharacteristicsRules?.isCustomDevice) {
    return heightOfOneRail;
  }

  return widgetWidth * (device.productSupportSpec.height / productSupportSpecWidth);
}

export const patchpanelIsPacedOnNoPatchpanelsRow = (row: IRow, movingDevice: IPlacedDevice): boolean => {
  return !row?.allowsPatchpanels && movingDevice?.device?.gridPlacementRules?.placeableOnMultimediaRails;
}

export const addCoverForPatchPanel = (device: IMovingDevice): IMovingDevice => {
  const isPatchPanelPipe = new IsPatchPanelPipe();
  if(!isPatchPanelPipe.transform(device)) {
    return;
  }
  device.options = device.options ?? [] as string[];
  if((device?.options?.indexOf(DeviceOptions.WITH_COVER) === -1) && (device?.options?.indexOf(DeviceOptions.NO_COVER) === -1)) {
    device?.options?.push(DeviceOptions.WITH_COVER);
  }
  return device;
}

export const getCoverOfDeviceOnSWB = (device: IProduct) => {
  return device?.presentationRules?.devicesSpecialCoverReference;
}

export const deviceHasRightSideExtension = (product: IProduct): boolean => {
  return !!product?.gridPlacementRules?.rightSideExtension;
}

export const getExtensionWidth = (product: IProduct): number => {
  return product?.gridPlacementRules?.rightSideExtension?.extensionWidth || 0;
}

export const gedDeviceWidthExtension = (productElement: IProduct): number => {
  if (deviceHasBridge({ device: productElement })) {
    return getBridgeWidth(productElement);
  }

  return getExtensionWidth(productElement);
}

export const widgetOnSwbFieldsWidth = (device: IProduct, widgetIsBridge: boolean, widgetIsRightExtension: boolean): number => {
  if (widgetIsBridge) {
    return getBridgeWidth(device);
  }

  if (widgetIsRightExtension) {
    return getExtensionWidth(device)
  }


  return getDevicesFieldsWidth(device);
}

export const getRightExtensionProductSupportSpecWidth = (device: IProduct): number => {
  return (device.productSupportSpec.width) * getExtensionWidth(device)
}

export const getRightExtensionLabelOffset = (rightExtensionWidht: number,
                                             widgetElementClientWidth: number,
                                             deviceWidth: number): number => {
  return rightExtensionWidht?
    (widgetElementClientWidth / (deviceWidth + deviceWidth * rightExtensionWidht)) * rightExtensionWidht : 0
}

export const getRotatedWidgetLabelPosition = (widgetElementClientHeight: number,
                                              widgetElementClientWidth: number,
                                              nativeElementHeight: number,
                                              nativeElementWidth: number,
                                              decreaseTopPositionInPercents: number): IPosition => {
  const decreaseTopBy = getPercentOf(decreaseTopPositionInPercents, widgetElementClientWidth);
  const top = widgetElementClientHeight / 2 - nativeElementHeight / 2;
  // top offset is subtracted from the left because widget is rotated
  const left = widgetElementClientWidth / 2 - nativeElementWidth / 2 - decreaseTopBy;

  return {top, left}
}

export const deviceRequiresNetworkAdditionalPlacementAccessories = (device: Partial<IDevice>): boolean => {
  return device?.productId === EASY_NET_DEVICE_ID ||
    device?.productId === PATCHPANEL_8_PORT_DEVICE_ID ||
    device?.productId === PATCHPANEL_16_PORT_DEVICE_ID ||
    device?.productId === PATCHPANEL_16_PORT_SHIELDED_DEVICE_ID;
}

export const deviceRequiresRailForPlacement = (device: Partial<IDevice>): boolean => {
  return device?.product?.productCharacteristicsRules?.isDirectMetering63A ||
    device?.product?.productCharacteristicsRules?.isDirectMetering125A ||
    device?.product?.productCharacteristicsRules?.isDirectMeteringCT ;
}


export const recalculateBOMCountForDevices = (device: Partial<IDevice>): boolean => {
  return deviceRequiresNetworkAdditionalPlacementAccessories(device) ||
    deviceRequiresRailForPlacement(device)
}

export const getSpecialPlacementDevicesLabelWidth = (parentElementClientWidth: number, productFieldsNumber: number): number => {
  if (!parentElementClientWidth) {
    return 0;
  }

  return (parentElementClientWidth / productFieldsNumber) * SPECIAL_DEVICES_LABELS_WIDTH_IN_PERCENTS;
}

export const disabledPropertyFound = (index: number,
                                      disabledProperties: string[],
                                      value: IDeviceConfigOptions,
                                      optionsArray: string): boolean => {
  return index > -1 && (disabledProperties?.findIndex(prop => prop === value[optionsArray][index].property) === -1)
}

const positionsAreEqual = (positionA: IPosition, positionB: IPosition): boolean => {
  return (positionA?.top === positionB?.top) && (positionA?.left === positionB?.left);
}

export const deviceIsNotMovedFromPreviousPosition = (device: IMovingDevice, newPosition: IDevicesPlacePosition): boolean => {
  return gridPositionIndexesAreEqual(newPosition?.gridIndex, device?.enclosureField) && positionsAreEqual({
    left: newPosition?.left,
    top: newPosition?.top
  }, device?.position)
}

export const thereAreDevicesThatAreNotMovedFromPreviousPositions = (placePositions: IDevicesPlacePosition[],
                                                                    movingDevices: IMovingDevice[]): boolean => {
  return placePositions.some(position =>
    deviceIsNotMovedFromPreviousPosition(movingDevices.find(d => d.uuid === position.devicesUUID), position));
}

export const deviceIsPlacedUnderCombBar = (device: IPlacedDevice, placedDevices: IPlacedDevice[]): boolean => {
  if (device?.device?.productCharacteristicsRules?.isCombBusBar) {
    // Comb-bus bar cannot be placed under another comb-bus bar
    return false;
  }
  return !!getCombBusbarPlacedOnTop(device, placedDevices);
}

export const getReplacingSldDeviceName = (device: IProduct, deviceFromRight: IProduct, language: string): string => {
  const replacingName = deviceFromRight.presentationRules.sldReplacingName
    .find(deviceToReplace => deviceToReplace.deviceFromRight.productRangeId === device.productRangeId &&
      deviceToReplace.deviceFromRight.productPoleId === device.poleId
    )?.replacingNameForDeviceFromRight[language];

  return replacingName?? device?.productCatalogueTitle;
}

export const removeFiVigiDevices = (allDevices: IPlacedDevice[]) => {
  return device => {
    const placedDevice = allDevices.find(d => d.id === device.deviceId)
    return !placedDevice?.device?.gridPlacementRules?.isDeviceSkippedFromSld;
  }
}

export const insertPlacedDevice = (placedDevices: IPlacedDevice[], newDevice: IRemovedDeviceFromSwitchboard): IPlacedDevice[] => {
  return [
    // part of the array before the specified index
    ...placedDevices.slice(0, newDevice.index),
    // inserted item
    newDevice.device,
    // part of the array after the specified index
    ...placedDevices.slice(newDevice.index)
  ];
}

export const getGridType = (productCharacteristicsRules: IProductCharacteristicsRules): DeviceOnGridType => {
  if (productCharacteristicsRules?.isCombBusBar) {
    return DeviceOnGridType.combBar;
  }

  if (productCharacteristicsRules?.isPowerTag) {
    return DeviceOnGridType.powerTag;
  }

  return DeviceOnGridType.device;
}

export const switchboardGridsFactory = (devicesEnclosureGrids: IGrid[],
                                        combEnclosureGrid: IGrid[],
                                        powerTagGrid: IGrid[]): ISwitchboardGrid[] => {
  return [
    { grid: devicesEnclosureGrids, gridType: DeviceOnGridType.device },
    { grid: combEnclosureGrid, gridType: DeviceOnGridType.combBar },
    { grid: powerTagGrid, gridType: DeviceOnGridType.powerTag }
  ]
}

const deviceCannotBeAttachedToPowerTag = (productCharacteristicsRules: IProductCharacteristicsRules): boolean => {
  return productCharacteristicsRules?.isPowerTag || productCharacteristicsRules?.isCombBusBar
}

const powerTagIsBetweenBordersOfDevice = (powerTag: IPlacedDevice, device: Partial<IPlacedDevice>): boolean => {
  if (powerTag.position.top !== device.position.top) {
    return false;
  }

  const powerTagRight = powerTag.position.left + powerTag.width;
  const devicesLeft = device?.position?.left;
  const devicesRight = device?.position?.left + device?.width;

  const bridgeWidth = getBridgeWidth(device?.device);
  if (bridgeWidth) {
    return (powerTag?.position?.left >= (devicesLeft - bridgeWidth)) && (powerTagRight === devicesRight);

  }

  return (powerTag?.position?.left >= devicesLeft) && (powerTagRight <= devicesRight);
}

export const getAttachedPowerTagOfDevice = (device: Partial<IPlacedDevice>, placedDevices: IPlacedDevice[]): IAttachedPowerTag => {
  if (deviceCannotBeAttachedToPowerTag(device?.device?.productCharacteristicsRules) || !device?.position) {
    return null;
  }

  const attachedPowerTag = new PowerTagDevicesPipe().transform(placedDevices, device?.enclosureField)
    ?.find(powerTag => powerTagIsBetweenBordersOfDevice(powerTag, device))

  if (!attachedPowerTag) {
    return null;
  }

  return {
    powerTagDevice: attachedPowerTag,
    positionOffset: attachedPowerTag.position?.left - device.position.left,
    attachedToDeviceUuid: device?.uuid
  }
}

export const getAttachedPowerTagsOfDevices = (devices: IPlacedDevice[], placedDevices: IPlacedDevice[]): IAttachedPowerTag[] => {
  const attachedPowerTags: IAttachedPowerTag[] = [];

  devices.forEach(device => {
    const attachedPowerTag = getAttachedPowerTagOfDevice(device, placedDevices);

    if (attachedPowerTag) {
      attachedPowerTags.push(attachedPowerTag);
    }
  });

  return attachedPowerTags;
}
